import React from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { AutoComboColumn, ComboOption, FAutoComboProps } from './autoCombo.models';
import {
  columnTemplate,
  comboOptionMatchesValue,
  defaultAutoComboLabel,
  getComboOptionValue,
  mergeComboOptions,
} from './autoCombo.helpers';
import { fetchAutoCombo, fetchAutoComboItem } from './autoCombo.api';

type State = {
  inputValue: string;
  options: ComboOption[];
  selectedOption: ComboOption | null;
  loading: boolean;
  loadingMore: boolean;
  pageOffset: number;
  count: number;
  hasMore: boolean;
  errorMessage: string;
};

const LOAD_MORE_SENTINEL = '__LOAD_MORE__';

class FAutoCombo extends React.Component<FAutoComboProps, State> {
  static defaultProps: Partial<FAutoComboProps> = {
    remote: true,
    pageLimit: 25,
    minimumSearchLength: 0,
    debounceMs: 300,
    fullWidth: true,
    size: 'small',
    displayMode: 'standard',
    itemAction: 'comboitem',
  };

  private debounceTimer?: ReturnType<typeof setTimeout>;
  private requestSequence = 0;

  state: State = {
    inputValue: '',
    options: this.props.options || [],
    selectedOption: null,
    loading: false,
    loadingMore: false,
    pageOffset: 0,
    count: 0,
    hasMore: false,
    errorMessage: '',
  };

  componentDidMount() {
    this.resolveInitialValue();

    if (this.props.remote && Number(this.props.minimumSearchLength ?? 0) === 0) {
      this.search('', true);
    } else if (!this.props.remote) {
      this.syncLocalSelection(this.props);
    }
  }

  componentDidUpdate(prevProps: FAutoComboProps) {
    if (prevProps.options !== this.props.options && !this.props.remote) {
      this.setState(
        { options: this.props.options || [] },
        () => this.syncLocalSelection(this.props),
      );
    }

    if (
      prevProps.value !== this.props.value ||
      prevProps.valueMode !== this.props.valueMode ||
      prevProps.service !== this.props.service
    ) {
      this.resolveInitialValue();
    }

    if (prevProps.params !== this.props.params && this.props.remote) {
      this.search(this.state.inputValue, true);
    }
  }

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.requestSequence += 1;
  }

  private getLabel = (option: ComboOption): string =>
    this.props.getOptionLabel
      ? this.props.getOptionLabel(option)
      : defaultAutoComboLabel(option);

  private syncLocalSelection = (props: FAutoComboProps) => {
    const selected =
      (props.options || []).find((option) =>
        comboOptionMatchesValue(option, props.value, props.valueMode),
      ) || null;

    this.setState({
      selectedOption: selected,
      inputValue: selected ? this.getLabel(selected) : '',
    });
  };

  private resolveInitialValue = async () => {
    const { value, valueMode } = this.props;

    if (value === null || value === undefined || value === '') {
      this.setState({ selectedOption: null, inputValue: '' });
      return;
    }

    const existing =
      this.state.options.find((option) =>
        comboOptionMatchesValue(option, value, valueMode),
      ) || null;

    if (existing) {
      this.setState({
        selectedOption: existing,
        inputValue: this.getLabel(existing),
      });
      return;
    }

    if (!this.props.remote) {
      this.syncLocalSelection(this.props);
      return;
    }

    try {
      const item = await fetchAutoComboItem(
        this.props.service,
        value,
        valueMode,
        this.props.params,
        this.props.itemAction,
      );

      if (!item) {
        this.setState({ selectedOption: null });
        return;
      }

      this.setState((state) => ({
        selectedOption: item,
        inputValue: this.getLabel(item),
        options: mergeComboOptions(state.options, [item]),
      }));
    } catch (error: any) {
      this.setState({
        errorMessage: error?.message || 'Unable to resolve selected value.',
      });
    }
  };

  private scheduleSearch = (search: string) => {
    if (!this.props.remote) return;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(
      () => this.search(search, true),
      Number(this.props.debounceMs ?? 300),
    );
  };

  private search = async (search: string, reset: boolean) => {
    if (!this.props.remote) return;

    const minimum = Number(this.props.minimumSearchLength ?? 0);

    if (search.trim().length < minimum) {
      this.setState({
        options: this.state.selectedOption ? [this.state.selectedOption] : [],
        pageOffset: 0,
        count: 0,
        hasMore: false,
      });
      return;
    }

    const sequence = ++this.requestSequence;
    const pageOffset = reset
      ? 0
      : this.state.pageOffset + Number(this.props.pageLimit ?? 25);

    this.setState({
      loading: reset,
      loadingMore: !reset,
      errorMessage: '',
    });

    try {
      const result = await fetchAutoCombo(this.props.service, {
        search,
        pageOffset,
        pageLimit: this.props.pageLimit,
        action: this.props.action,
        params: this.props.params,
      });

      if (sequence !== this.requestSequence) return;

      this.setState((state) => ({
        options: reset
          ? mergeComboOptions(
              state.selectedOption ? [state.selectedOption] : [],
              result.options,
            )
          : mergeComboOptions(state.options, result.options),
        pageOffset: result.pageOffset,
        count: result.count,
        hasMore: result.hasMore,
      }));
    } catch (error: any) {
      if (sequence !== this.requestSequence) return;

      this.setState({
        errorMessage: error?.message || 'Unable to load lookup options.',
      });
    } finally {
      if (sequence === this.requestSequence) {
        this.setState({ loading: false, loadingMore: false });
      }
    }
  };

  private handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string,
  ) => {
    this.setState({ inputValue: value });

    if (reason === 'input') this.scheduleSearch(value);

    if (reason === 'clear') {
      this.props.onChange(null, null);
    }
  };

  private handleChange = (
    _event: React.SyntheticEvent,
    option: ComboOption | null,
  ) => {
    if (option?.raw?.__sentinel === LOAD_MORE_SENTINEL) {
      this.search(this.state.inputValue, false);
      return;
    }

    this.setState({
      selectedOption: option,
      inputValue: option ? this.getLabel(option) : '',
    });

    if (!option) {
      this.props.onChange(null, null);
      return;
    }

    this.props.onChange(
      getComboOptionValue(option, this.props.valueMode),
      option,
    );
  };

  private optionsWithLoadMore = (): ComboOption[] => {
    if (!this.state.hasMore) return this.state.options;

    return [
      ...this.state.options,
      {
        rid: null,
        rix: null,
        shc: '',
        nam: 'Load more results…',
        dsc: '',
        raw: { __sentinel: LOAD_MORE_SENTINEL },
      },
    ];
  };

  private renderDetailedOption = (
    option: ComboOption,
    columns: AutoComboColumn[],
  ) => {
    if (option.raw?.__sentinel === LOAD_MORE_SENTINEL) {
      return (
        <Box sx={{ width: '100%', py: 0.5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {this.state.loadingMore ? 'Loading…' : option.nam}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: columnTemplate(columns),
          gap: 1.5,
          width: '100%',
          alignItems: 'center',
          py: 0.5,
        }}
      >
        {columns.map((column) => {
          const value = column.valueGetter
            ? column.valueGetter(option)
            : (option as any)[column.field] ??
              option.raw?.[column.field] ??
              '';

          return (
            <Typography
              key={column.field}
              variant="body2"
              noWrap
              sx={{
                textAlign: column.align || 'left',
                fontWeight: column.field === 'shc' ? 700 : 400,
              }}
            >
              {value}
            </Typography>
          );
        })}
      </Box>
    );
  };

  render() {
    const detailed =
      this.props.displayMode === 'detailed' &&
      (this.props.columns || []).length > 0;

    return (
      <Autocomplete
        value={this.state.selectedOption}
        inputValue={this.state.inputValue}
        options={this.optionsWithLoadMore()}
        loading={this.state.loading}
        disabled={this.props.disabled}
        fullWidth={this.props.fullWidth}
        filterOptions={this.props.remote ? (options) => options : undefined}
        getOptionLabel={(option) =>
          option.raw?.__sentinel === LOAD_MORE_SENTINEL
            ? option.nam
            : this.getLabel(option)
        }
        isOptionEqualToValue={(option, value) =>
          comboOptionMatchesValue(
            option,
            getComboOptionValue(value, this.props.valueMode),
            this.props.valueMode,
          )
        }
        onInputChange={this.handleInputChange}
        onChange={this.handleChange}
        renderOption={(props, option) => (
          <li {...props}>
            {detailed
              ? this.renderDetailedOption(option, this.props.columns || [])
              : option.raw?.__sentinel === LOAD_MORE_SENTINEL
              ? (
                  <ListItemText
                    primary={this.state.loadingMore ? 'Loading…' : option.nam}
                    primaryTypographyProps={{
                      fontWeight: 700,
                      textAlign: 'center',
                    }}
                  />
                )
              : (
                  <ListItemText
                    primary={this.getLabel(option)}
                    secondary={option.dsc || undefined}
                  />
                )}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={this.props.label}
            placeholder={this.props.placeholder}
            required={this.props.required}
            error={this.props.error || Boolean(this.state.errorMessage)}
            helperText={this.state.errorMessage || this.props.helperText}
            size={this.props.size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(this.state.loading || this.state.loadingMore) && (
                    <CircularProgress size={18} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }
}

export default FAutoCombo;
